package com.ijoin.ui.components

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.nestedscroll.NestedScrollConnection
import androidx.compose.ui.input.nestedscroll.NestedScrollSource
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.Velocity
import androidx.compose.ui.unit.dp
import androidx.compose.ui.platform.LocalDensity


@Stable
class PullToRefreshState internal constructor(
    initialRefreshing: Boolean,
    private val onRefresh: () -> Unit,
    private val canRefresh: () -> Boolean,
    private val refreshThreshold: Float,
    private val maxPullDistance: Float
) {
    private var pullOffset by mutableFloatStateOf(0f)
    var isRefreshing by mutableStateOf(initialRefreshing)
        private set

    val progress: Float
        get() = if (isRefreshing) 1f else (pullOffset / refreshThreshold).coerceIn(0f, 1f)

    val contentOffset: Float
        get() = if (isRefreshing) refreshThreshold / 3f else pullOffset / 2f

    fun handleDrag(delta: Float): Float {
        if (delta > 0f) {
            if (!canRefresh() || isRefreshing) return 0f
            val newOffset = (pullOffset + delta).coerceIn(0f, maxPullDistance)
            val consumed = newOffset - pullOffset
            pullOffset = newOffset
            return consumed
        } else if (delta < 0f && pullOffset > 0f) {
            val newOffset = (pullOffset + delta).coerceAtLeast(0f)
            val consumed = newOffset - pullOffset
            pullOffset = newOffset
            return consumed
        }
        return 0f
    }

    fun release() {
        val shouldRefresh = pullOffset >= refreshThreshold && !isRefreshing
        pullOffset = 0f
        if (shouldRefresh) {
            isRefreshing = true
            onRefresh()
        }
    }

    fun updateRefreshing(refreshing: Boolean) {
        isRefreshing = refreshing
        if (!refreshing) {
            pullOffset = 0f
        }
    }
}

@Composable
fun rememberPullToRefreshState(
    refreshing: Boolean,
    onRefresh: () -> Unit,
    canRefresh: () -> Boolean,
    refreshThreshold: Dp = 96.dp,
    maxPullDistance: Dp = 160.dp
): PullToRefreshState {
    val density = LocalDensity.current
    val thresholdPx = with(density) { refreshThreshold.toPx() }
    val maxDistancePx = with(density) { maxPullDistance.toPx() }
    val state = remember {
        PullToRefreshState(
            initialRefreshing = refreshing,
            onRefresh = onRefresh,
            canRefresh = canRefresh,
            refreshThreshold = thresholdPx,
            maxPullDistance = maxDistancePx
        )
    }
    LaunchedEffect(refreshing) {
        state.updateRefreshing(refreshing)
    }
    return state
}

fun Modifier.pullToRefresh(state: PullToRefreshState?): Modifier {
    if (state == null) return this
    return this.then(
        Modifier.nestedScroll(object : NestedScrollConnection {
            override fun onPreScroll(available: Offset, source: NestedScrollSource): Offset {
                if (source != NestedScrollSource.Drag) return Offset.Zero
                val consumed = state.handleDrag(available.y)
                return if (consumed != 0f) Offset(0f, consumed) else Offset.Zero
            }

            override fun onPostScroll(consumed: Offset, available: Offset, source: NestedScrollSource): Offset {
                if (source != NestedScrollSource.Drag) return Offset.Zero
                val consumedDelta = state.handleDrag(available.y)
                return if (consumedDelta != 0f) Offset(0f, consumedDelta) else Offset.Zero
            }

            override suspend fun onPreFling(available: Velocity): Velocity {
                state.release()
                return Velocity.Zero
            }
        })
    )
}

@Composable
fun RefreshableContainer(
    modifier: Modifier = Modifier,
    state: PullToRefreshState?,
    indicatorAlignment: Alignment = Alignment.TopCenter,
    enabled: Boolean = true,
    content: @Composable BoxScope.(Modifier) -> Unit
) {
    if (!enabled || state == null) {
        Box(modifier = modifier) {
            content(Modifier)
        }
    } else {
        Box(
            modifier = modifier.pullToRefresh(state)
        ) {
            val translatedModifier = Modifier.graphicsLayer {
                translationY = state.contentOffset
            }
            content(translatedModifier)
            PullToRefreshIndicator(
                state = state,
                modifier = Modifier
                    .align(indicatorAlignment)
                    .padding(top = 12.dp)
            )
        }
    }
}

@Composable
fun PullToRefreshIndicator(
    state: PullToRefreshState,
    modifier: Modifier = Modifier
) {
    val showIndicator = state.isRefreshing || state.progress > 0f
    if (!showIndicator) return
    Surface(
        modifier = modifier,
        shape = MaterialTheme.shapes.large,
        tonalElevation = 3.dp,
        color = MaterialTheme.colorScheme.surface
    ) {
        val indicatorModifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp)
        if (state.isRefreshing) {
            CircularProgressIndicator(
                strokeWidth = 2.dp,
                modifier = indicatorModifier,
                color = MaterialTheme.colorScheme.primary
            )
        } else {
            CircularProgressIndicator(
                progress = state.progress,
                strokeWidth = 2.dp,
                modifier = indicatorModifier,
                color = MaterialTheme.colorScheme.primary
            )
        }
    }
}

fun LazyListState.isAtTop(): Boolean =
    firstVisibleItemIndex == 0 && firstVisibleItemScrollOffset == 0
